import { createLogger } from '@svenskaspelab/nova-core/log';
import { throttle } from 'lodash';
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';
import { type PersistStorage, type StorageValue } from 'zustand/middleware';

const logger = createLogger('poolbetting.zustand');

function assertStorageValue(data: unknown): asserts data is StorageValue<unknown> {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid data type');
  }

  if (!('state' in data)) {
    throw new Error('Invalid data structure, state is missing');
  }
}

const THROTTLE_WAIT_MS = 1000;

// eslint-disable-next-line unicorn/no-null
const NOD_DATA = null;

type ReadFn<TStorage> = (data: unknown) => TStorage | undefined;
type WriteFn<TStorage> = (data: TStorage, callback: (data: TStorage | undefined) => void) => void;

/**
 * Curried Zustand middleware function to generalize local storage persistence in tipsen
 * Receives read and write functions to handle the storage.
 *
 * @param read Reader function to read the storage
 * @param write Writer function to write the storage
 * @returns Zustand store middleware
 */
export function persistLocalStorage<TStorage>(
  read: ReadFn<TStorage>,
  write: WriteFn<TStorage>
): PersistStorage<TStorage> {
  const throttleWrite = throttle(write, THROTTLE_WAIT_MS);

  return {
    getItem(key: string): StorageValue<TStorage> | null {
      const lsData = localStorage.getItem(key);
      if (!lsData) {
        return NOD_DATA;
      }

      try {
        const uncompressed = decompressFromUTF16(lsData);
        const parsedJson: unknown = JSON.parse(uncompressed);

        assertStorageValue(parsedJson);
        const storage = read(parsedJson.state);

        if (storage === undefined) {
          return NOD_DATA;
        }

        return {
          state: storage,
          version: parsedJson.version,
        };
      } catch (error) {
        // fail silently if data from local storage isn't stringified JSON
        logger.warn('Failed to parse data from local storage', { error, key });
      }

      return NOD_DATA;
    },
    setItem(key: string, value: StorageValue<TStorage>): void {
      throttleWrite(value.state, (data) => {
        const stateToPersist = { ...value, state: data };

        const dataToPersist = JSON.stringify(stateToPersist);

        logger.debug('Persisting to local storage', { key, size: dataToPersist.length });

        localStorage.setItem(key, compressToUTF16(dataToPersist));
      });

      // localStorage.setItem(name, compressToUTF16(JSON.stringify(value)));
    },
    removeItem(key: string): void {
      localStorage.removeItem(key);
    },
  };
}
